import * as React from 'react';
import DateTimePickerRoot, { BaseDateTimePickerProps } from './DateTimePickerRoot';
import KeyboardDateInput, { KeyboardDateInputProps } from '../_shared/KeyboardDateInput';
import { useUtils } from '../_shared/hooks/useUtils';
import { toShowDateTimePickerTabs } from '../_helpers/utils';
import { ExtendWrapper, Wrapper } from '../wrappers/Wrapper';
import { dateTimePickerDefaultProps } from '../constants/prop-types';
import { DateValidationProps, getError, pick12hOr24hFormat } from '../_helpers/text-field-helper';
import {
  BaseKeyboardPickerProps,
  useKeyboardPickerState,
} from '../_shared/hooks/useKeyboardPickerState';

export type KeyboardDateTimePickerProps = BaseDateTimePickerProps &
  DateValidationProps &
  BaseKeyboardPickerProps &
  ExtendWrapper<KeyboardDateInputProps>;

export function KeyboardDateTimePicker(props: KeyboardDateTimePickerProps) {
  const {
    allowKeyboardControl,
    ampm,
    animateYearScrolling,
    autoOk,
    autoSubmit,
    dateRangeIcon,
    disableFuture,
    disablePast,
    format,
    forwardedRef,
    initialFocusedDate,
    invalidDateMessage,
    labelFunc,
    leftArrowIcon,
    leftArrowButtonProps,
    maxDate,
    maxDateMessage,
    minDate,
    onOpen,
    onClose,
    minDateMessage,
    minutesStep,
    onAccept,
    onChange,
    onMonthChange,
    onYearChange,
    openTo,
    renderDay,
    rightArrowIcon,
    rightArrowButtonProps,
    shouldDisableDate,
    showTabs,
    timeIcon,
    value,
    variant,
    ...other
  } = props;

  const utils = useUtils();
  const toShowTabs = toShowDateTimePickerTabs(showTabs);
  const { pickerProps, inputProps, wrapperProps } = useKeyboardPickerState(props, {
    getValidationError: () => getError(value, utils, props as any),
    getDefaultFormat: () =>
      pick12hOr24hFormat(format, ampm, {
        '12h': utils.dateTime12hFormat,
        '24h': utils.dateTime24hFormat,
      }),
  });

  return (
    <Wrapper
      variant={variant}
      showTabs={showTabs}
      InputComponent={KeyboardDateInput}
      DateInputProps={inputProps}
      {...wrapperProps}
      {...other}
    >
      <DateTimePickerRoot
        {...pickerProps}
        ampm={ampm}
        allowKeyboardControl={allowKeyboardControl}
        minutesStep={minutesStep}
        animateYearScrolling={animateYearScrolling}
        autoSubmit={autoSubmit}
        dateRangeIcon={dateRangeIcon}
        disableFuture={disableFuture}
        disablePast={disablePast}
        leftArrowIcon={leftArrowIcon}
        leftArrowButtonProps={leftArrowButtonProps}
        maxDate={maxDate}
        minDate={minDate}
        onMonthChange={onMonthChange}
        onYearChange={onYearChange}
        openTo={openTo}
        renderDay={renderDay}
        rightArrowIcon={rightArrowIcon}
        rightArrowButtonProps={rightArrowButtonProps}
        shouldDisableDate={shouldDisableDate}
        showTabs={toShowTabs}
        timeIcon={timeIcon}
      />
    </Wrapper>
  );
}

KeyboardDateTimePicker.defaultProps = dateTimePickerDefaultProps;

export default React.forwardRef((props: KeyboardDateTimePickerProps, ref) => (
  <KeyboardDateTimePicker {...props} forwardedRef={ref} />
));
